import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axios/axiosInstance';

const EMPTY_FORM = {
    name: '', addressLine1: '', addressLine2: '',
    city: '', state: '', zip: '', phone: '', email: '',
};

const Field = ({ label, children }) => (
    <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
            {label}
        </label>
        {children}
    </div>
);

const Input = ({ value, onChange, placeholder }) => (
    <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
            width: '100%', padding: '8px 10px', borderRadius: 7,
            border: '1px solid #d1d5db', fontSize: 13,
            outline: 'none', boxSizing: 'border-box',
        }}
    />
);

const Spinner = () => (
    <div style={{ width: 16, height: 16, border: '2px solid #1B75F0', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
);

const Locations = () => {
    const companyId = localStorage.getItem('companyId');
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [isNew, setIsNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [locUsers, setLocUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [creatingUser, setCreatingUser] = useState(false);
    const [createdCreds, setCreatedCreds] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchLocations = async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/companies/${companyId}/locations`);
            setLocations(res.data);
        } catch {
            showToast('Failed to load locations.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLocations(); }, [companyId]);

    const openLocation = (loc) => {
        setIsNew(false);
        setSelected(loc);
        setCreatedCreds(null);
        setNewUserEmail('');
        fetchLocUsers(loc.id);
        setForm({
            name:         loc.name         || '',
            addressLine1: loc.addressLine1 || '',
            addressLine2: loc.addressLine2 || '',
            city:         loc.city         || '',
            state:        loc.state        || '',
            zip:          loc.zip          || '',
            phone:        loc.phone        || '',
            email:        loc.email        || '',
        });
    };

    const openNew = () => {
        setIsNew(true);
        setSelected({});
        setForm(EMPTY_FORM);
    };

    const closePanel = () => { setSelected(null); setIsNew(false); };
    const setField = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

    const handleSave = async () => {
        if (!form.name.trim()) { showToast('Location name is required.', 'error'); return; }
        setSaving(true);
        try {
            const payload = {
                name:         form.name         || null,
                addressLine1: form.addressLine1 || null,
                addressLine2: form.addressLine2 || null,
                city:         form.city         || null,
                state:        form.state        || null,
                zip:          form.zip          || null,
                phone:        form.phone        || null,
                email:        form.email        || null,
            };
            if (isNew) {
                const res = await axiosInstance.post(`/companies/${companyId}/locations`, payload);
                setLocations(prev => [...prev, res.data]);
                showToast(`Location "${res.data.name}" created.`);
                closePanel();
            } else {
                const res = await axiosInstance.put(`/companies/${companyId}/locations/${selected.id}`, payload);
                setLocations(prev => prev.map(l => l.id === selected.id ? { ...l, ...res.data } : l));
                showToast('Location updated.');
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to save location.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (loc) => {
        if (!window.confirm(`Delete location "${loc.name}"?`)) return;
        try {
            await axiosInstance.delete(`/companies/${companyId}/locations/${loc.id}`);
            setLocations(prev => prev.filter(l => l.id !== loc.id));
            if (selected?.id === loc.id) closePanel();
            showToast(`"${loc.name}" deleted.`);
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete location.', 'error');
        }
    };

    const handleToggleActive = async (loc) => {
        try {
            await axiosInstance.put(`/companies/${companyId}/locations/${loc.id}`, { isActive: !loc.isActive });
            setLocations(prev => prev.map(l => l.id === loc.id ? { ...l, isActive: !loc.isActive } : l));
        } catch {
            showToast('Failed to update status.', 'error');
        }
    };

    const fetchLocUsers = useCallback(async (locId) => {
        if (!locId || !companyId) return;
        setLoadingUsers(true);
        try {
            const res = await axiosInstance.get(`/companies/${companyId}/locations/${locId}/users`);
            setLocUsers(Array.isArray(res.data) ? res.data : []);
        } catch {
            setLocUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    }, [companyId]);

    const handleCreateLocUser = async (locId) => {
        if (!newUserEmail.trim()) { showToast('Email is required.', 'error'); return; }
        setCreatingUser(true);
        setCreatedCreds(null);
        try {
            const res = await axiosInstance.post(`/companies/${companyId}/locations/${locId}/users`, { email: newUserEmail.trim() });
            setCreatedCreds(res.data);
            setNewUserEmail('');
            fetchLocUsers(locId);
            showToast('Location user created.');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to create user.', 'error');
        } finally {
            setCreatingUser(false);
        }
    };

    const handleRevokeLocUser = async (locId, userId) => {
        if (!window.confirm('Deactivate this location user?')) return;
        try {
            await axiosInstance.delete(`/companies/${companyId}/locations/${locId}/users/${userId}`);
            setLocUsers(prev => prev.filter(u => u.id !== userId));
            showToast('User deactivated.');
        } catch {
            showToast('Failed to deactivate user.', 'error');
        }
    };

    return (
        <div style={{ padding: '0 4px' }}>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: 0 }}>Locations</h2>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>
                        Manage company locations. Each location can have its own login users.
                    </p>
                </div>
                <button
                    onClick={openNew}
                    style={{
                        background: 'var(--brand-primary, #1B75F0)', color: '#fff',
                        border: 'none', borderRadius: 8, padding: '9px 18px',
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                >
                    + Add Location
                </button>
            </div>

            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* List */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60 }}><Spinner /></div>
                    ) : locations.length === 0 ? (
                        <div style={{
                            background: '#fff', borderRadius: 12, border: '2px dashed #e2e8f0',
                            padding: 48, textAlign: 'center',
                        }}>
                            <div style={{ fontSize: 32, marginBottom: 12 }}>📍</div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 }}>No locations yet</div>
                            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
                                Add locations to run payroll by office, store, or branch.
                            </div>
                            <button
                                onClick={openNew}
                                style={{ background: 'var(--brand-primary, #1B75F0)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            >
                                Add First Location
                            </button>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                            {locations.map(loc => (
                                <div
                                    key={loc.id}
                                    onClick={() => openLocation(loc)}
                                    style={{
                                        background: '#fff', borderRadius: 12,
                                        border: `2px solid ${selected?.id === loc.id ? 'var(--brand-primary, #1B75F0)' : '#e2e8f0'}`,
                                        padding: '16px 18px', cursor: 'pointer',
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (selected?.id !== loc.id) e.currentTarget.style.borderColor = '#d1d5db'; }}
                                    onMouseLeave={e => { if (selected?.id !== loc.id) e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <div style={{
                                                width: 36, height: 36, borderRadius: 10,
                                                background: 'var(--brand-primary-light, #1B75F018)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 16,
                                            }}>📍</div>
                                            <div>
                                                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{loc.name}</div>
                                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                                                    {loc.userCount} user{loc.userCount !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
                                            <button
                                                onClick={() => handleToggleActive(loc)}
                                                style={{
                                                    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, border: 'none', cursor: 'pointer',
                                                    background: loc.isActive ? '#f0fdf4' : '#fef2f2',
                                                    color: loc.isActive ? '#16a34a' : '#dc2626',
                                                }}
                                            >
                                                {loc.isActive ? 'Active' : 'Inactive'}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(loc)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16, padding: '2px 4px', borderRadius: 4 }}
                                                onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                                                onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
                                            >×</button>
                                        </div>
                                    </div>
                                    {(loc.city || loc.state) && (
                                        <div style={{ fontSize: 12, color: '#64748b' }}>
                                            {[loc.addressLine1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                                        </div>
                                    )}
                                    {loc.phone && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{loc.phone}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Edit / Create panel */}
                {selected !== null && (
                    <div style={{ width: 320, flexShrink: 0, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                                {isNew ? 'New Location' : form.name || 'Edit Location'}
                            </span>
                            <button onClick={closePanel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20 }}>×</button>
                        </div>

                        <div style={{ padding: '16px 18px', maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                            <Field label="Location Name *">
                                <Input value={form.name} onChange={setField('name')} placeholder="Dallas Office" />
                            </Field>
                            <Field label="Address Line 1">
                                <Input value={form.addressLine1} onChange={setField('addressLine1')} placeholder="123 Main St" />
                            </Field>
                            <Field label="Address Line 2">
                                <Input value={form.addressLine2} onChange={setField('addressLine2')} placeholder="Suite 100" />
                            </Field>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                                <Field label="City">
                                    <Input value={form.city} onChange={setField('city')} placeholder="Dallas" />
                                </Field>
                                <Field label="State">
                                    <Input value={form.state} onChange={setField('state')} placeholder="TX" />
                                </Field>
                                <Field label="ZIP">
                                    <Input value={form.zip} onChange={setField('zip')} placeholder="75201" />
                                </Field>
                            </div>
                            <Field label="Phone">
                                <Input value={form.phone} onChange={setField('phone')} placeholder="(214) 555-0100" />
                            </Field>
                            <Field label="Email">
                                <Input value={form.email} onChange={setField('email')} placeholder="dallas@company.com" />
                            </Field>
                        </div>

                        <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
                            <button onClick={handleSave} disabled={saving} style={{
                                flex: 1, background: 'var(--brand-primary, #1B75F0)', color: '#fff',
                                border: 'none', borderRadius: 8, padding: '9px 0', fontSize: 13,
                                fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 6,
                            }}>
                                {saving ? <><Spinner /> Saving…</> : isNew ? 'Create Location' : 'Save Changes'}
                            </button>
                            <button onClick={closePanel} style={{ padding: '9px 14px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569' }}>
                                Cancel
                            </button>
                        </div>

                        {/* Location Users section — only shown when editing an existing location */}
                        {!isNew && selected?.id && (
                            <div style={{ borderTop: '2px solid #f1f5f9' }}>
                                <div style={{ padding: '12px 18px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Login Users</span>
                                    <a href={`/login/location`} target="_blank" rel="noreferrer"
                                        style={{ fontSize: 11, color: '#0891b2', textDecoration: 'none' }}>
                                        Login URL: /login/location
                                    </a>
                                </div>

                                {createdCreds && (
                                    <div style={{ margin: '0 18px 10px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
                                        <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>User created — share these credentials:</div>
                                        <div><strong>Email:</strong> {createdCreds.email}</div>
                                        <div><strong>Temp Password:</strong> <code style={{ background: '#dcfce7', padding: '1px 5px', borderRadius: 4 }}>{createdCreds.tempPassword}</code></div>
                                        <div style={{ color: '#6b7280', marginTop: 4, fontSize: 11 }}>User must change password on first login.</div>
                                    </div>
                                )}

                                <div style={{ padding: '0 18px 10px' }}>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <input
                                            value={newUserEmail}
                                            onChange={e => setNewUserEmail(e.target.value)}
                                            placeholder="user@company.com"
                                            style={{ flex: 1, padding: '7px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 12, outline: 'none' }}
                                        />
                                        <button
                                            onClick={() => handleCreateLocUser(selected.id)}
                                            disabled={creatingUser}
                                            style={{ background: '#0891b2', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        >
                                            {creatingUser ? '…' : '+ Add User'}
                                        </button>
                                    </div>
                                </div>

                                <div style={{ padding: '0 18px 14px', maxHeight: 160, overflowY: 'auto' }}>
                                    {loadingUsers ? (
                                        <div style={{ textAlign: 'center', padding: 12 }}><Spinner /></div>
                                    ) : locUsers.length === 0 ? (
                                        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '10px 0' }}>
                                            No login users yet. Add one above.
                                        </div>
                                    ) : (
                                        locUsers.map(u => (
                                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{u.email}</div>
                                                    <div style={{ fontSize: 10, color: u.isActive ? '#16a34a' : '#dc2626' }}>{u.isActive ? 'Active' : 'Inactive'}</div>
                                                </div>
                                                <button
                                                    onClick={() => handleRevokeLocUser(selected.id, u.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 14, padding: '2px 4px' }}
                                                    onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                                                    onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
                                                    title="Deactivate user"
                                                >×</button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Locations;
